/**
 * THE FEATURE REGISTRY — the single array the app composes itself from.
 *
 * To add a module: import its FeatureModule and append it here. That is the ONLY router-side edit a
 * feature requires. The router (router.tsx), side-nav (AppShell), and command palette all read this
 * array — they never know individual features by name.
 *
 * Keep this list in build-order; it doubles as the module manifest.
 */
import { assertFeatureModule, type FeatureModule } from "./featureModule";

// ── Feature modules (add new ones below; one line each) ────────────────────────
import dashboardModule from "@/features/dashboard";
import receptionModule from "@/features/reception";
import samplingModule from "@/features/sampling";
import lisModule from "@/features/lis";
import validationModule from "@/features/validation";
import resultsModule from "@/features/results";
import financeModule from "@/features/finance";
import inventoryModule from "@/features/inventory";
import qcModule from "@/features/qc";
import referralModule from "@/features/referral";
import analyticsModule from "@/features/analytics";
import aiExplanationModule from "@/features/ai-explanation";
import aiAssistantModule from "@/features/ai-assistant";
import patientPortalModule from "@/features/patient-portal";
import authModule from "@/features/auth";
import adminModule from "@/features/admin";
import managementModule from "@/features/management";

const MODULES: FeatureModule[] = [
  dashboardModule,
  receptionModule,
  samplingModule,
  lisModule,
  validationModule,
  resultsModule,
  financeModule,
  inventoryModule,
  qcModule,
  referralModule,
  analyticsModule,
  aiExplanationModule,
  aiAssistantModule,
  patientPortalModule,
  authModule,
  adminModule, // 14 — lab-owner ERP cockpit (profile/équipe/journal/tarification/abonnement)
  managementModule, // 14 — cross-lab PLATFORM super-admin console (onboarding/paramtypes/abonnements/support); platform-admin gated
  // catalogue, iam, audit, ... (append future modules here, one line each)
];

// Validate + run each module's onRegister hook exactly once, at import time.
const seen = new Set<string>();
for (const m of MODULES) {
  assertFeatureModule(m);
  if (seen.has(m.id)) throw new Error(`Duplicate FeatureModule id: ${m.id}`);
  seen.add(m.id);
  m.onRegister?.();
}

export const FEATURES: ReadonlyArray<FeatureModule> = MODULES;
